import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Menu, X, GraduationCap, Home, Download } from "lucide-react";
import { toast } from "sonner";
import { promptInstall, isAppInstalled } from "@/lib/install";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCategories } from "@/context/CategoriesContext";
import { LOGO } from "@/lib/api";

export function Header() {
  const { count, setDrawerOpen } = useCart();
  const { user } = useAuth();
  const { categories } = useCategories();
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const NAV = [
    { to: "/shop", label: "All Products" },
    ...categories.map((c) => ({ to: `/shop/${c.slug}`, label: c.name })),
    { to: "/schools", label: "Assorted Schools" },
    { to: "/stokvel", label: "Stationery Stokvel" },
    { to: "/specials", label: "Specials", gold: true },
  ];

  const MOBILE_NAV = [{ to: "/", label: "Home" }, ...NAV];

  const search = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/shop?q=${encodeURIComponent(q.trim())}` : "/shop");
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className="bg-navy-dark text-white/70 text-center text-xs font-medium tracking-wide py-1.5 px-4">
        Your school essentials, all in one place — online orders · shop collection · delivery options
      </div>
      <div className="bg-white/90 backdrop-blur-xl border-b border-slate-200 text-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-3 sm:gap-5 h-16">
          <button
            className="lg:hidden p-2 -ml-2"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Menu"
          >
            <Menu size={22} />
          </button>
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
            <img src={LOGO} alt="BTMEDU" className="h-9 w-auto" />
          </Link>
          <form onSubmit={search} className="hidden md:flex flex-1 max-w-xl" data-testid="header-search-form">
            <div className="relative w-full">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search stationery, textbooks, ISBN…"
                className="w-full h-10 rounded-full bg-slate-100 text-navy text-sm pl-10 pr-24 outline-none placeholder:text-slate-400 border border-transparent focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand/30 transition-colors"
                data-testid="header-search-input"
              />
              <button
                type="submit"
                className="absolute right-1 top-1 h-8 px-4 rounded-full bg-brand hover:bg-brand-accent text-white text-xs font-bold transition-colors"
                data-testid="header-search-button"
              >
                Search
              </button>
            </div>
          </form>
          <div className="flex items-center gap-1.5 ml-auto">
            <Link
              to="/"
              className="lg:hidden p-2 rounded-full text-slate-600 hover:text-navy hover:bg-slate-100 transition-colors"
              data-testid="mobile-home-button"
              aria-label="Home"
            >
              <Home size={20} />
            </Link>
            {user ? (
              <Link
                to="/account"
                className="hidden sm:flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-full text-slate-600 hover:text-navy hover:bg-slate-100 transition-colors"
                data-testid="header-account-link"
              >
                <User size={17} />
                <span className="hidden xl:inline">My Account</span>
              </Link>
            ) : (
              <>
                <Link
                  to="/auth"
                  className="hidden sm:flex items-center text-sm font-bold px-3 py-2 rounded-full text-navy hover:bg-slate-100 transition-colors"
                  data-testid="header-login-link"
                >
                  Login
                </Link>
                <Link
                  to="/auth?mode=register"
                  className="hidden sm:flex items-center text-sm font-bold px-4 py-2 rounded-full bg-gold hover:bg-gold-hover text-white transition-colors"
                  data-testid="header-register-link"
                >
                  Register
                </Link>
                <Link
                  to="/auth"
                  className="sm:hidden p-2 rounded-full text-slate-600 hover:text-navy hover:bg-slate-100 transition-colors"
                  data-testid="header-account-link"
                  aria-label="Login or register"
                >
                  <User size={17} />
                </Link>
              </>
            )}
            <button
              onClick={() => setDrawerOpen(true)}
              className="relative flex items-center gap-2 bg-navy hover:bg-navy-dark text-white font-bold text-sm px-4 py-2 rounded-full transition-colors"
              data-testid="header-cart-button"
            >
              <ShoppingCart size={17} />
              <span className="hidden sm:inline">Cart</span>
              <span
                className="min-w-5 h-5 px-1 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center"
                data-testid="header-cart-count"
              >
                {count}
              </span>
            </button>
          </div>
        </div>
        <nav className="hidden lg:block border-t border-slate-200/80" data-testid="main-nav">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-1 overflow-x-auto">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/shop"}
                className={({ isActive }) =>
                  `whitespace-nowrap text-[13px] font-medium px-3 py-2.5 border-b-2 transition-colors ${
                    isActive
                      ? "border-brand text-brand"
                      : `border-transparent ${n.gold ? "text-brand" : "text-slate-600"} hover:text-navy`
                  }`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
      {menuOpen && (
        <div className="lg:hidden bg-white text-navy border-b border-slate-200 shadow-xl" data-testid="mobile-menu">
          <div className="flex items-center justify-between px-4 pt-3">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Menu</span>
            <button
              onClick={() => setMenuOpen(false)}
              className="p-2 -mr-2 rounded-full text-slate-500 hover:text-navy hover:bg-slate-100 transition-colors"
              data-testid="mobile-menu-close"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>
          <form onSubmit={search} className="px-4 pb-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search stationery, textbooks, ISBN…"
              className="w-full h-11 rounded-xl bg-slate-100 text-navy text-sm px-4 outline-none border border-transparent focus:border-brand"
              data-testid="mobile-search-input"
            />
          </form>
          <nav className="px-2 pb-4 flex flex-col">
            {MOBILE_NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMenuOpen(false)}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-100 flex items-center gap-2 ${n.gold ? "text-brand" : ""}`}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {n.to === "/" && <Home size={15} className="text-slate-400" />}
                {n.label}
              </Link>
            ))}
            {user ? (
              <Link
                to="/account"
                onClick={() => setMenuOpen(false)}
                className="px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-100 flex items-center gap-2"
                data-testid="mobile-nav-account"
              >
                <GraduationCap size={16} /> My Account
              </Link>
            ) : (
              <>
                <Link
                  to="/auth"
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-100 flex items-center gap-2"
                  data-testid="mobile-nav-login"
                >
                  <User size={16} className="text-slate-400" /> Login
                </Link>
                <Link
                  to="/auth?mode=register"
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2.5 rounded-lg text-sm font-bold text-brand hover:bg-brand/10 flex items-center gap-2"
                  data-testid="mobile-nav-register"
                >
                  <GraduationCap size={16} /> Register Now
                </Link>
              </>
            )}
            {!isAppInstalled() && (
              <button
                onClick={async () => {
                  setMenuOpen(false);
                  const outcome = await promptInstall();
                  if (outcome === "unavailable") {
                    toast.info("To install: open your browser menu and choose 'Install app' or 'Add to Home screen'.");
                  }
                }}
                className="px-3 py-2.5 rounded-lg text-sm font-bold text-brand hover:bg-brand/10 flex items-center gap-2 text-left"
                data-testid="mobile-nav-install"
              >
                <Download size={16} /> Install BTMEDU App
              </button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
